// Webclient → Calls. The logged-in user's own call history on /api/portal/calls,
// with 3CX-style filters (All / Missed / Received / Placed), client-side search,
// and inline recording playback (authed blob).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, fetchAudioUrl } from '../lib/api';
import { useActiveTenant } from '../lib/tenantScope';

type Call = {
  call_uuid: string; direction: 'inbound' | 'outbound'; caller_id_name: string; caller_id_number: string;
  destination_number: string; start_stamp: string; duration: number; billsec: number;
  has_recording: boolean; summary: string | null; sentiment: string | null;
  peer_name?: string | null; // resolved name of the other party (internal ext -> person)
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'missed', label: 'Missed' },
  { key: 'received', label: 'Received' },
  { key: 'placed', label: 'Placed' },
];

function fmtWhen(iso: string) {
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}
function fmtDur(sec: number) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const arrow = (c: Call) => {
  const missed = c.direction === 'inbound' && !c.billsec;
  const d = c.direction === 'outbound'
    ? 'M7 17L17 7M17 7H8M17 7V16'
    : 'M17 7L7 17M7 17H16M7 17V8';
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={missed ? 'text-danger' : c.direction === 'outbound' ? 'text-success' : 'text-textSecondary'} stroke="currentColor">
      <path d={d} />
    </svg>
  );
};

export default function Calls() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [playing, setPlaying] = useState<string | null>(null);
  const tenant = useActiveTenant();
  const calls = useQuery({ queryKey: ['portal-calls', tenant, filter], queryFn: () => api.portal.calls(filter) });

  const play = async (uuid: string) => {
    setPlaying(uuid);
    try {
      const url = await fetchAudioUrl(`/api/portal/recording/${uuid}`);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlaying(null); };
      await audio.play();
    } catch { setPlaying(null); }
  };

  const q = search.trim().toLowerCase();
  const visible: Call[] = (calls.data || []).filter((c: Call) =>
    !q ||
    (c.peer_name || '').toLowerCase().includes(q) ||
    (c.caller_id_name || '').toLowerCase().includes(q) ||
    (c.caller_id_number || '').toLowerCase().includes(q) ||
    (c.destination_number || '').toLowerCase().includes(q)
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header: filter pills + search */}
      <div className="shrink-0 flex items-center gap-1 px-3 h-12 border-b border-border">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1.5 rounded-lg text-sm shrink-0 ${filter === f.key ? 'bg-elevated text-primary' : 'text-textSecondary hover:bg-elevated'}`}>
            {f.label}
          </button>
        ))}
        <div className="flex-1 relative ml-1">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="search" placeholder="Search name or number…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1 text-xs bg-elevated rounded-lg border border-border/50 focus:outline-none focus:border-primary text-text placeholder:text-textSecondary"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {calls.isLoading && <div className="p-4 text-sm text-textSecondary">Loading…</div>}
        {!calls.isLoading && visible.length === 0 && (
          <div className="p-4 text-sm text-textSecondary">{search ? 'No matching calls.' : 'No calls.'}</div>
        )}
        {visible.map((c: Call) => {
          const peer = c.direction === 'outbound' ? c.destination_number : (c.caller_id_number || c.caller_id_name);
          return (
            <div key={c.call_uuid} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-elevated">
              <div className="shrink-0">{arrow(c)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.peer_name || (c.caller_id_name && c.direction === 'inbound' ? c.caller_id_name : peer)}</div>
                <div className="text-xs text-textSecondary truncate">{peer}{c.summary ? ` · ${c.summary}` : ''}</div>
              </div>
              <div className="shrink-0 text-xs text-textSecondary text-right">
                <div>{fmtWhen(c.start_stamp)}</div>
                <div>{fmtDur(c.billsec)}</div>
              </div>
              {c.has_recording && (
                <button onClick={() => play(c.call_uuid)} disabled={playing === c.call_uuid} title="Play recording"
                  className="shrink-0 p-2 rounded-lg text-textSecondary hover:text-primary hover:bg-bg disabled:opacity-50">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
